import { GoogleGenerativeAI } from "@google/generative-ai";
import mermaid from "mermaid";

// Initialize Gemini API
let genAI: GoogleGenerativeAI | null = null;

const getGeminiAPI = () => {
  if (!genAI) {
    const apiKey = localStorage.getItem("GEMINI_API_KEY");
    if (!apiKey) {
      throw new Error("Gemini API key not found. Please set it in settings.");
    }
    genAI = new GoogleGenerativeAI(apiKey);
  }
  return genAI;
};

export const setGeminiApiKey = (apiKey: string) => {
  localStorage.setItem("GEMINI_API_KEY", apiKey);
  genAI = new GoogleGenerativeAI(apiKey);
};

export const getGeminiApiKey = (): string | null => {
  return localStorage.getItem("GEMINI_API_KEY");
};

export const hasGeminiApiKey = (): boolean => {
  return !!localStorage.getItem("GEMINI_API_KEY");
};

export const clearGeminiApiKey = () => {
  localStorage.removeItem("GEMINI_API_KEY");
  genAI = null;
};

export const testGeminiApiKey = async (
  apiKey: string,
): Promise<{ success: boolean; message: string }> => {
  try {
    const testAI = new GoogleGenerativeAI(apiKey);
    const model = testAI.getGenerativeModel({ model: "gemini-2.5-flash" });

    // Make a simple test request
    const result = await model.generateContent("Say 'API key is valid'");
    const response = await result.response;
    const text = response.text();

    if (text) {
      return {
        success: true,
        message: "Connection successful! API key is valid.",
      };
    }
    return {
      success: false,
      message: "Invalid API key or no response from Gemini.",
    };
  } catch (error: any) {
    return {
      success: false,
      message: error.message || "Failed to connect to Gemini API",
    };
  }
};

// Initialize Mermaid
mermaid.initialize({
  startOnLoad: false,
  theme: "default",
  securityLevel: "loose",
});

export interface TextToDiagramOptions {
  description: string;
  diagramType?: "flowchart" | "sequence" | "class" | "state" | "er" | "auto";
}

export interface DiagramToCodeOptions {
  imageBase64: string;
  texts: string[];
  theme: string;
}

export interface PresentationSlide {
  title: string;
  content: string;
  speakerNotes: string;
  elements: string[]; // Element IDs to show in this slide
  order: number;
}

export interface PresentationData {
  title: string;
  slides: PresentationSlide[];
  theme: string;
}

/**
 * Generate Mermaid diagram from text description using Gemini
 */
export const generateMermaidFromText = async (
  options: TextToDiagramOptions,
): Promise<string> => {
  try {
    const ai = getGeminiAPI();
    const model = ai.getGenerativeModel({ model: "gemini-2.5-flash" });

    const diagramTypePrompt =
      options.diagramType && options.diagramType !== "auto"
        ? `Create a ${options.diagramType} diagram.`
        : "Choose the most appropriate diagram type.";

    const prompt = `Generate a Mermaid diagram based on this description: "${options.description}".
    
${diagramTypePrompt}

Rules:
- Return ONLY the Mermaid diagram code, nothing else
- Do not include markdown code blocks or backticks
- Start directly with the diagram type (e.g., "flowchart TD", "sequenceDiagram", etc.)
- Ensure the syntax is valid Mermaid code
- Keep it clean and well-structured
- IMPORTANT: If node labels contain special characters like parentheses (), brackets [], or other punctuation, you MUST quote them using double quotes or use bracket notation
- Example: Instead of 'A --> B (for async)', use 'A --> B["for async"]' or 'A -->|for async| B'
- Avoid using parentheses directly in node labels without proper quoting`;

    const result = await model.generateContent(prompt);
    const response = await result.response;
    let mermaidCode = response.text().trim();

    // Clean up the response
    mermaidCode = mermaidCode.replace(/```mermaid\n?/g, "");
    mermaidCode = mermaidCode.replace(/```\n?/g, "");
    mermaidCode = mermaidCode.trim();

    // Validate Mermaid syntax
    try {
      await mermaid.parse(mermaidCode);
    } catch (error) {
      console.error("Invalid Mermaid syntax:", error);
      throw new Error("Generated Mermaid code is invalid");
    }

    return mermaidCode;
  } catch (error: any) {
    console.error("Error generating Mermaid diagram:", error);
    throw new Error(error.message || "Failed to generate diagram from text");
  }
};

/**
 * Convert Mermaid code to SVG
 */
export const convertMermaidToSVG = async (
  mermaidCode: string,
): Promise<string> => {
  try {
    const { svg } = await mermaid.render("mermaid-preview", mermaidCode);
    return svg;
  } catch (error: any) {
    console.error("Error converting Mermaid to SVG:", error);
    throw new Error("Failed to convert Mermaid to SVG");
  }
};

/**
 * Generate HTML code from diagram image and text using Gemini
 */
export const generateCodeFromDiagram = async (
  options: DiagramToCodeOptions,
): Promise<string> => {
  try {
    const ai = getGeminiAPI();
    const model = ai.getGenerativeModel({ model: "gemini-2.5-flash" });

    const prompt = `Analyze this UI/UX diagram and generate clean, modern HTML/CSS code.

Text elements found in the diagram: ${options.texts.join(", ")}
Theme: ${options.theme}

Requirements:
- Generate semantic HTML5 with embedded CSS
- Match the layout and design from the diagram
- Use modern CSS (Flexbox/Grid)
- Make it responsive
- Include the theme colors (${
      options.theme === "dark" ? "dark theme" : "light theme"
    })
- Ensure all text elements are included
- Return only the complete HTML code, nothing else`;

    const imagePart = {
      inlineData: {
        data: options.imageBase64.split(",")[1],
        mimeType: "image/jpeg",
      },
    };

    const result = await model.generateContent([prompt, imagePart]);
    const response = await result.response;
    let htmlCode = response.text().trim();

    // Clean up markdown code blocks if present
    htmlCode = htmlCode.replace(/```html\n?/g, "");
    htmlCode = htmlCode.replace(/```\n?/g, "");
    htmlCode = htmlCode.trim();

    return htmlCode;
  } catch (error: any) {
    console.error("Error generating code from diagram:", error);
    throw new Error(error.message || "Failed to generate code from diagram");
  }
};

/**
 * Analyze text and suggest diagram type using Gemini
 */
export const suggestDiagramType = async (
  text: string,
): Promise<"flowchart" | "sequence" | "class" | "state" | "er"> => {
  try {
    const ai = getGeminiAPI();
    const model = ai.getGenerativeModel({ model: "gemini-2.0-flash-exp" });

    const prompt = `Analyze this text and suggest the most appropriate Mermaid diagram type: "${text}"

Choose from:
- flowchart (for processes, workflows, decision trees)
- sequence (for interactions, API calls, message flows)
- class (for object-oriented designs, data structures)
- state (for state machines, lifecycle diagrams)
- er (for entity relationships, database schemas)

Return ONLY one word: flowchart, sequence, class, state, or er`;

    const result = await model.generateContent(prompt);
    const response = await result.response;
    const suggestion = response.text().trim().toLowerCase() as
      | "flowchart"
      | "sequence"
      | "class"
      | "state"
      | "er";

    if (
      ["flowchart", "sequence", "class", "state", "er"].includes(suggestion)
    ) {
      return suggestion;
    }

    return "flowchart"; // Default fallback
  } catch (error) {
    console.error("Error suggesting diagram type:", error);
    return "flowchart"; // Default fallback
  }
};

/**
 * Generate diagram explanation using Gemini
 */
export const explainDiagram = async (mermaidCode: string): Promise<string> => {
  try {
    const ai = getGeminiAPI();
    const model = ai.getGenerativeModel({ model: "gemini-2.0-flash-exp" });

    const prompt = `Explain this Mermaid diagram in simple terms:

${mermaidCode}

Provide a clear, concise explanation of what the diagram represents and its key components.`;

    const result = await model.generateContent(prompt);
    const response = await result.response;
    return response.text().trim();
  } catch (error: any) {
    console.error("Error explaining diagram:", error);
    throw new Error("Failed to explain diagram");
  }
};

/**
 * Generate presentation slides from diagram using Gemini
 */
export const generatePresentationFromDiagram = async (options: {
  imageBase64: string;
  texts: string[];
  elementCount: number;
  theme: string;
}): Promise<PresentationData> => {
  try {
    const ai = getGeminiAPI();
    const model = ai.getGenerativeModel({ model: "gemini-2.5-flash" });

    const prompt = `Analyze this diagram and create a presentation with multiple slides.

Text elements in diagram: ${options.texts.join(", ")}
Total elements: ${options.elementCount}
Theme: ${options.theme}

Create a logical presentation structure that:
1. Starts with an overview slide
2. Breaks down the diagram into 3-5 focused slides
3. Each slide reveals parts of the diagram progressively
4. Ends with a summary/conclusion slide

For each slide provide:
- Title (short, descriptive)
- Content (2-3 bullet points explaining this part)
- Speaker notes (detailed talking points for presenter, 2-3 sentences)

Return ONLY valid JSON in this exact format:
{
  "title": "Presentation Title",
  "slides": [
    {
      "title": "Slide Title",
      "content": "Bullet point 1\\nBullet point 2\\nBullet point 3",
      "speakerNotes": "Detailed speaker notes for this slide.",
      "order": 1
    }
  ]
}`;

    const imagePart = {
      inlineData: {
        data: options.imageBase64.split(",")[1],
        mimeType: "image/jpeg",
      },
    };

    const result = await model.generateContent([prompt, imagePart]);
    const response = await result.response;
    let jsonText = response.text().trim();

    // Clean up markdown code blocks
    jsonText = jsonText.replace(/```json\n?/g, "");
    jsonText = jsonText.replace(/```\n?/g, "");
    jsonText = jsonText.trim();

    const presentationData: PresentationData = JSON.parse(jsonText);
    presentationData.theme = options.theme;

    return presentationData;
  } catch (error: any) {
    console.error("Error generating presentation:", error);
    throw new Error(
      error.message || "Failed to generate presentation from diagram",
    );
  }
};
